<section
  class="dashboard-page"
  [class.wa-sidebar-expanded]="waUi.sidebarExpanded()"
>
  <header class="page-header">
    <nav class="wa-breadcrumb">
      <a routerLink="/" queryParamsHandling="preserve">Web Analytics</a>
      <span class="sep">/</span>
      <a routerLink="/users" queryParamsHandling="preserve">Users</a>
      <span class="sep">/</span>
      <span class="here">{{ detail()?.username ?? '…' }}</span>
    </nav>
    <p-button
      type="button"
      label="Back to Users"
      icon="pi pi-arrow-left"
      [outlined]="true"
      size="small"
      class="mb-2"
      (onClick)="backToUsers()"
    />
    @if (detail(); as u) {
      <h1>{{ u.username }}</h1>
      @if (nexusProfileLoading()) {
        <p class="user-identity muted">Loading directory profile…</p>
      } @else if (nexusProfile(); as profile) {
        <p class="user-identity">
          @if (profile.name) {
            <span>{{ profile.name }}</span>
          }
          @if (profile.name && profile.email) {
            <span class="identity-sep">·</span>
          }
          @if (profile.email) {
            <span class="mono">{{ profile.email }}</span>
          }
        </p>
      }
      <div class="quick-stats">
        <span>Events: <strong>{{ u.totalEvents | number }}</strong></span>
        <span>Sessions: <strong>{{ u.sessions | number }}</strong></span>
        <span>First: <strong>{{ u.firstSeen | slice: 0:19 }}</strong></span>
        <span>Last: <strong>{{ u.lastSeen | slice: 0:19 }}</strong></span>
        <span>Device: <strong>{{ u.primaryDevice }}</strong></span>
        <span>Browser: <strong>{{ u.primaryBrowser }}</strong></span>
        <span>OS: <strong>{{ u.primaryOs }}</strong></span>
        <span class="quick-stat-location">
          Location:
          <strong>
            <app-country-label [country]="u.primaryCountry" />
            / {{ u.primaryCity }}
          </strong>
        </span>
      </div>
    }
  </header>

  @if (loading()) {
    <div class="wa-loading-bar" role="status" aria-live="polite">
      <p-progressSpinner strokeWidth="4" styleClass="wa-loading-spinner" />
      <span>Loading data for selected range…</span>
    </div>
  }

  @if (loading() && !detail()) {
    <p-progressSpinner strokeWidth="3" />
  } @else if (error(); as err) {
    <p-message severity="error" [text]="err" />
  } @else if (detail(); as u) {
    <div [class.wa-content-stale]="loading()">
    <p-card header="Activity over time" styleClass="wa-chart-card">
      @if (activityData(); as ad) {
        <div class="chart-box chart-box--tall">
          <p-chart type="line" [data]="ad" [options]="activityOptions() ?? {}" />
        </div>
      } @else {
        <p class="empty-hint">No activity</p>
      }
    </p-card>

    <h2 class="section-title">Sessions</h2>
    @for (s of sessions(); track s.sessionId) {
      <details class="session-block">
        <summary class="session-summary">
          <span class="mono">{{ s.startTime | slice: 0:19 }}</span>
          <span>{{ s.eventCount | number }} events</span>
          <span class="muted"
            >{{ s.durationSeconds | number }}s · {{ s.device }} ·
            {{ s.browser }}</span
          >
        </summary>
        <ol class="timeline">
          @for (ev of s.events; track $index) {
            <li class="timeline-row">
              <span class="t-time mono">{{ ev.timestamp | slice: 11:19 }}</span>
              <p-tag
                [value]="ev.eventType"
                [severity]="eventSeverity(ev.eventType)"
              />
              <span
                class="t-path"
                [pTooltip]="ev.path"
                tooltipPosition="top"
                >{{ humanPath(ev.path) }}</span
              >
              @if (ev.path !== humanPath(ev.path)) {
                <span class="muted small">{{ truncPath(ev.path) }}</span>
              }
              @if (ev.extra) {
                <pre class="extra-snippet">{{ ev.extra }}</pre>
              }
            </li>
          }
        </ol>
      </details>
    } @empty {
      <p class="empty-hint">No sessions</p>
    }

    <div class="charts-split">
      <p-card header="Event types" styleClass="wa-chart-card">
        @if (typeBarData(); as tb) {
          <div class="chart-box chart-box--mid">
            <p-chart type="bar" [data]="tb" [options]="typeBarOptions() ?? {}" />
          </div>
        }
      </p-card>
      <p-card header="Top pages" styleClass="wa-table-card">
        <p-table [value]="topPaths()" styleClass="wa-paths-table">
          <ng-template pTemplate="header">
            <tr>
              <th>Page</th>
              <th class="col-count">Count</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr
              class="path-row"
              (click)="navigateFilterPath(row.path)"
              (keyup.enter)="navigateFilterPath(row.path)"
              tabindex="0"
            >
              <td>
                {{ humanPath(row.path) }}
                <span class="muted small block">{{ truncPath(row.path) }}</span>
              </td>
              <td class="col-count">{{ row.count | number }}</td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    </div>

    <p-card header="All events" styleClass="wa-table-card">
      <div class="events-toolbar">
        <input
          pInputText
          [(ngModel)]="eventsFilterType"
          placeholder="Filter event type (substring)"
          class="wa-f-input"
          (keyup.enter)="applyEventsFilter()"
        />
        <p-button
          label="Apply filter"
          size="small"
          (onClick)="applyEventsFilter()"
        />
        <span class="events-loaded muted">
          Loaded {{ loadedSummary().loaded | number }} of
          {{ loadedSummary().total | number }} events
        </span>
        @if (hasMore()) {
          <p-button
            label="Load more data"
            icon="pi pi-angle-double-down"
            size="small"
            [outlined]="true"
            [loading]="loadingMore()"
            (onClick)="loadMore()"
          />
        }
      </div>
      <p-table
        [value]="accumulatedEvents()"
        [paginator]="true"
        [rows]="eventsPageSize"
        [first]="eventsFirst"
        [loading]="loading()"
        (onPage)="onEventsPage($event)"
        styleClass="wa-paths-table"
      >
        <ng-template pTemplate="header">
          <tr>
            <th>Time</th>
            <th>Type</th>
            <th>Path</th>
            <th>Device</th>
            <th>Extra</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-ev>
          <tr>
            <td class="mono">{{ ev.timestamp | slice: 0:19 }}</td>
            <td>{{ ev.eventType }}</td>
            <td class="path-cell">{{ truncPath(ev.path) }}</td>
            <td>{{ ev.device }}</td>
            <td class="extra-cell">{{ ev.extra }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="5">No events</td>
          </tr>
        </ng-template>
      </p-table>
    </p-card>
    </div>
  }
</section>
